/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Theme } from '@mui/material';
import { Box, Grid } from '@mui/material';
import React from 'react';
import { CustomTooltip } from '@opengeoweb/shared';
import type { uiTypes } from '@opengeoweb/store';
import {
  CloudLoading,
  ColumnCollapse,
  ColumnCollapsed,
} from '@opengeoweb/theme';
import { useCoreTranslation } from '../../../utils/i18n';
import type { LayerManagerCustomSettings } from '../LayerManagerUtils';
import { columnClasses, leftButtonsStyle } from '../LayerManagerUtils';
import { LayerSelectButton } from '../LayerSelect/LayerSelectButton';

interface RowItemProps {
  className: string;
  title: string;
  onToggleCollapsed: (name: string) => void;
  collapsedColumns?: Record<string, boolean>;
}

const RowItemCollapse: React.FC<RowItemProps> = ({
  className,
  title,
  collapsedColumns,
  onToggleCollapsed = (): void => {},
}: RowItemProps) => {
  const { t } = useCoreTranslation();
  const hoverId = `${columnClasses.columnHeader}-${className}`;

  return (
    <Box
      className={`${columnClasses.columnHeader} ${className}`}
      id={hoverId}
      style={{
        ...(collapsedColumns?.[className] && {
          width: '24px',
          flexGrow: 0,
        }),
      }}
    >
      <CustomTooltip
        key={collapsedColumns?.[className] ? 'Expand' : 'Collapse'}
        title={
          collapsedColumns?.[className]
            ? t('column-expand', { title })
            : t('column-collapse')
        }
      >
        <Grid
          className={`${columnClasses.columnHeader} ${className}`}
          onClick={() => {
            onToggleCollapsed(className);
            document
              .getElementById(hoverId)
              ?.dispatchEvent(new Event('mouseleave'));
          }}
          style={{
            ...(collapsedColumns?.[className] && {
              width: '24px',
              flexGrow: 0,
            }),
          }}
        >
          {collapsedColumns?.[className] ? (
            <ColumnCollapsed />
          ) : (
            <>
              <Box sx={styles.text}>{title}</Box>
              <ColumnCollapse className="column-hover-icon" />
            </>
          )}
        </Grid>
      </CustomTooltip>
    </Box>
  );
};

interface DescriptionRowProps extends React.ComponentProps<typeof Grid> {
  source?: uiTypes.Source;
  settings?: LayerManagerCustomSettings['header'];
  addLayerComponent?: React.ReactElement;
  onToggleCollapsed?: (name: string) => void;
  collapsedColumns?: Record<string, boolean>;
}

const styles = {
  text: (theme: Theme): React.CSSProperties => ({
    paddingLeft: '10px',
    fontSize: '12px',
    fontFamily: 'Roboto',
    opacity: 0.67,
    ...theme.palette.geowebColors.layerManager.headerRowText,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
};

const DescriptionRow: React.FC<DescriptionRowProps> = ({
  settings,
  addLayerComponent,
  onToggleCollapsed = (): void => {},
  collapsedColumns,
  ...gridProps
}: DescriptionRowProps) => {
  const { t } = useCoreTranslation();

  return (
    <Grid
      alignItems="center"
      className="descriptionRow"
      container
      data-testid="descriptionRow"
      {...gridProps}
    >
      <Grid
        className={columnClasses.column1}
        sx={{
          ...leftButtonsStyle,
          justifyContent: 'center',
          paddingRight: '6px',
        }}
      >
        {addLayerComponent || (
          <LayerSelectButton
            icon={settings?.addLayer?.icon}
            tooltipTitle={settings?.addLayer?.tooltipTitle}
          />
        )}
      </Grid>
      <Grid className="medium-layermanager columnLayerName">
        <Box sx={styles.text}>
          {settings?.layerName?.title ?? t('layermanager-layer')}
        </Box>
      </Grid>
      <RowItemCollapse
        className={columnClasses.columnService}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={onToggleCollapsed}
        title={settings?.layerService?.title ?? t('layermanager-service')}
      />
      <RowItemCollapse
        className={columnClasses.column2}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={onToggleCollapsed}
        title={settings?.layerName?.title ?? t('layermanager-layer')}
      />
      <RowItemCollapse
        className={columnClasses.column3}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={onToggleCollapsed}
        title={settings?.layerStyle?.title ?? t('layermanager-style-title')}
      />
      <RowItemCollapse
        className={columnClasses.column4}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={onToggleCollapsed}
        title={settings?.opacity?.title ?? t('layermanager-opacity-title')}
      />
      <RowItemCollapse
        className={columnClasses.column5}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={onToggleCollapsed}
        title={
          settings?.dimensions?.title ?? t('layermanager-dimensions-title')
        }
      />
      <RowItemCollapse
        className={columnClasses.acceptanceTime}
        collapsedColumns={collapsedColumns}
        onToggleCollapsed={onToggleCollapsed}
        title={
          settings?.acceptanceTime?.title ?? t('layermanager-acceptance-time')
        }
      />
      <Grid className={columnClasses.loadDuration}>
        <Box
          sx={[styles.text, { display: 'flex', justifyContent: 'flex-end' }]}
        >
          <CloudLoading />
        </Box>
      </Grid>
      <Grid className={columnClasses.column6} />
    </Grid>
  );
};

export default DescriptionRow;
